import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Button } from 'reactstrap';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';

import { common } from 'plato-js-client';
import {
    Actions,
    checklistEditorReducer,
    serialiseChecklist,
    unserialiseChecklistForInitialState,
} from './checklist-editor-reducer';

import { ChecklistEditorContext, ChecklistEditorDispatchContext } from './checklist-editor-context';
import { EditableGroup } from './EditableGroup';

import './checklist-editor.css';

const dndModifiers = [ restrictToVerticalAxis ];

export function ChecklistEditor({
    children,
    checklist,
    onSave,
}) {
    const [ loaded, setLoaded ] = useState(false);
    const [ state, dispatch ] = useReducer(checklistEditorReducer, {
        saving: false,

        title: '',
        inactive: false,

        groupsById: {},
        optionsByQuestionId: {},
        questionsById: {},

        groupOrder: [],
        questionOrderByGroupId: {},
        optionOrderByQuestionId: {},
    });

    const groupOrder = state.groupOrder;
    const showEmptyStateMessage = groupOrder.length < 1;

    useEffect(() => {
        dispatch({
            type: Actions.INITIAL_LOAD,
            payload: {
                title: checklist.name,
                inactive: checklist.inactive,
                ...unserialiseChecklistForInitialState(checklist.json),
            },
        });

        setLoaded(true);
    }, [ checklist.json ]);

    const createGroup = () => dispatch({
        type: Actions.CREATE_GROUP,
    });

    const saveChecklist = useCallback(async () => {
        if (state.saving) {
            return;
        }

        dispatch({
            type: Actions.TOGGLE_SAVING,
            payload: {
                saving: true,
            },
        });

        const updatedChecklist = new common.PmsChecklist(checklist.id);
        updatedChecklist.json = JSON.stringify(serialiseChecklist(state));
        updatedChecklist.name = state.title;
        updatedChecklist.inactive = state.inactive;

        await updatedChecklist.update();

        onSave(updatedChecklist);

        dispatch({
            type: Actions.TOGGLE_SAVING,
            payload: {
                saving: false,
            },
        });
    }, [ state ]);

    const saveChecklistOnHotkey = (e) => {
        if (!e.ctrlKey) {
            return;
        }

        if (e.key !== 's') {
            return;
        }

        e.preventDefault();
        return saveChecklist();
    };

    useEffect(() => {
        document.addEventListener('keydown', saveChecklistOnHotkey);

        return () => {
            document.removeEventListener('keydown', saveChecklistOnHotkey);
        };
    }, [ saveChecklistOnHotkey, state ]);

    const sortGroup = useCallback(({
        active,
        over,
    }) => {
        if (!over || active.id === over.id) {
            return;
        }

        const from = groupOrder.indexOf(active.id);
        const to = groupOrder.indexOf(over.id);

        dispatch({
            type: Actions.SORT_GROUPS,
            payload: {
                from,
                to,
            },
        });
    }, [ groupOrder, checklist.id ]);

    const renderedListOfGroups = useMemo(() => groupOrder.map((groupId, index) => (
        <EditableGroup
            key={ `group-${ groupId }` }
            groupId={ groupId }
            groupIndex={ index }
        />
    )), [ groupOrder ]);

    if (!loaded) {
        return (
            <>Preparing checklist editor...</>
        );
    }

    return (<ChecklistEditorContext.Provider value={ state }>
        <ChecklistEditorDispatchContext.Provider value={ dispatch }>
            { state.saving ? <div className={ 'checklist-editor__saving-overlay' }>
                <p>Saving, please wait...</p>
            </div> : null }

            { children ? children : null }

            <div className={ 'checklist-editor checklist-editor__panel' }>
                <div className={ 'checklist-editor__panel-body' }>
                    { showEmptyStateMessage &&
                        <p className={ 'checklist-editor__empty-message' }>This checklist has no groups, add one to get started.</p> }

                    <DndContext
                        onDragEnd={ sortGroup }
                        modifiers={ dndModifiers }
                        collisionDetection={ closestCenter }
                    >
                        <SortableContext items={ groupOrder }>
                            { renderedListOfGroups }
                        </SortableContext>
                    </DndContext>
                </div>

                <div className={ 'checklist-editor__panel-foot checklist-editor__editor-foot' }>
                    <div className={ 'checklist-editor__actions' }>
                        <Button
                            outline
                            color={ 'primary' }
                            onClick={ createGroup }
                        >
                            Add Group
                        </Button>
                        <Button
                            color={ 'primary' }
                            onClick={ saveChecklist }
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        </ChecklistEditorDispatchContext.Provider>
    </ChecklistEditorContext.Provider>);
}
